// src/lib/dbMutate.js
//
// Hardening Sprint Track 1.2 — mutation wrapper with automatic cache
// invalidation.
//
// Before this, each component cleared the SWR cache by hand after a
// mutation: some called cache.clear('events'), some called the new
// db.bustCache(...), some forgot entirely. The net effect was a long
// tail of "I made a change but the UI didn't update" bugs that only
// reproduced after a hard refresh (or after the SWR background fetch
// happened to land on the next read).
//
// The wrapper accepts a table name (or a list of tables for multi-table
// writes), runs a callback that does the actual supabase call, and on
// success clears every IndexedDB cache store the table maps to. Callers
// keep the full supabase fluent builder — we don't try to re-invent
// .update().eq() chains.
//
// Usage:
//
//   const { data, error } = await mutate('event_players', () =>
//     supabase.from('event_players').insert(rows).select()
//   )
//
//   // multi-table mutation (e.g., a FRAGO that writes bounties + phases +
//   // objectives in sequence — pass all three so every related cache
//   // store is busted at the end):
//   await mutate(['bounties', 'frago_phases', 'frago_objectives'], async () => {
//     await supabase.from('bounties').insert(...)
//     await supabase.from('frago_phases').insert(...)
//     return supabase.from('frago_objectives').insert(...)
//   })
//
// TABLE_CACHE_MAP maps a mutation TABLE (the database table being
// written) to the IndexedDB CACHE STORES that should be flushed — those
// are the IndexedDB names the SWR readers populate. The Hardening Sprint
// design doc lists semantic keys (events / roster / sitrep / ...);
// those names are tracked here as comments on each entry so future
// auditors can correlate the doc with the actual code.

import { supabase } from './supabaseClient'
import cache from './localCache'
import { formatSupabaseError } from './errorMessages'

// One entry per database table that gets written from app code. Tables
// missing from this map skip the auto-clear step — callers can still
// pass options.bustStores explicitly if they need to flush a store the
// map doesn't know about.
export const TABLE_CACHE_MAP = {
  // Roster writes echo through every event-level surface that displays
  // a player (events list, identity card, faction scoreboard).
  event_players:     ['events', 'event_players', 'event_factions'],

  // Bounties + FRAGOs live in the same table; FRAGO phases / objectives
  // also count as "bounty-affecting" because the FragoCard / MISSIONS
  // tab embed their phase counts.
  bounties:          ['bounties'],
  frago_phases:      ['bounties'],
  frago_objectives:  ['bounties'],
  frago_trigger_qr:  ['bounties'],
  bounty_pursuits:   ['bounty_pursuits'],
  bounty_guards:    [],
  bounty_notes:     [],

  // Kill log writes pull player_stats forward via the confirm/dismiss
  // RPCs — invalidate both so the scorecard reflects fresh totals.
  kill_log:          ['kill_log', 'player_stats'],

  // Team membership powers the player card + identity card + roster.
  team_members:      ['team_members', 'event_players'],
  teams:             ['team_members'],

  // MCS membership routes back into the identity card (faction +
  // node label). The IndexedDB cache doesn't have a dedicated MCS
  // store today — event_players is the join target the readers walk.
  mcs_nodes:         ['event_players'],
  mcs_node_members:  ['event_players'],

  // Event-level writes drive Dashboard, OrgAdmin, SITREP.
  events:            ['events'],
  event_factions:    ['event_factions'],
  event_checkpoints: [],
  event_pauses:      [],

  // Org-level writes drive OrgAdmin + the org dropdown on event create.
  organizations:     ['users'],
  org_members:       ['org_members', 'users'],

  // Stats / profile updates feed the scorecard + IdentityCard.
  player_stats:      ['player_stats'],
  users:             ['users'],
}

// Mutate one or more tables and bust their cache stores on success.
//
//   tables:   string or string[] of database tables being written
//   queryFn:  async function that runs the actual supabase call and
//             returns its { data, error } result
//   options:
//     bustStores: extra IndexedDB store names to flush (useful for
//                 mutations whose effects span tables not in
//                 TABLE_CACHE_MAP — e.g., a kill that should also flush
//                 the bounties cache because it claimed a kill bounty)
//
// Returns whatever queryFn returned. On error, no cache writes happen.
export async function mutate(tables, queryFn, options = {}) {
  const tableList = Array.isArray(tables) ? tables : [tables]
  const result = await queryFn()
  if (!result?.error) {
    const stores = new Set()
    tableList.forEach(t => (TABLE_CACHE_MAP[t] || []).forEach(s => stores.add(s)))
    ;(options.bustStores || []).forEach(s => stores.add(s))
    await Promise.all([...stores].map(s => cache.clear(s).catch(() => {})))
  } else {
    // Track 5.2 — attach a user-friendly translation of the Postgres /
    // Supabase error so callers can `setMessage(result.friendlyMessage)`
    // without re-implementing the mapping at every call site.
    result.friendlyMessage = formatSupabaseError(result.error)
  }
  return result
}

// RPC mutations don't fire postgres_changes, so callers that route a
// write through an RPC have to bust caches manually. This helper is the
// canonical place to do it — pass the database tables the RPC mutates
// and any explicit cache stores that need flushing alongside.
//
//   const { data, error } = await rpc('confirm_kill_with_stats',
//     { p_kill_id: id },
//     { tables: ['kill_log', 'player_stats'] }
//   )
export async function rpc(name, args, { tables = [], bustStores = [] } = {}) {
  const result = await supabase.rpc(name, args)
  if (!result?.error) {
    const stores = new Set()
    tables.forEach(t => (TABLE_CACHE_MAP[t] || []).forEach(s => stores.add(s)))
    bustStores.forEach(s => stores.add(s))
    await Promise.all([...stores].map(s => cache.clear(s).catch(() => {})))
  } else {
    result.friendlyMessage = formatSupabaseError(result.error)
  }
  return result
}

export default mutate
